import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Plus, RefreshCw, Receipt, CheckCircle, Eye, Upload } from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassModal,
  GlassTextarea,
  FadeIn,
} from '../../components/ui';
import { api } from '../../hooks/useApi';

const INITIAL_CARD = {
  nome: '',
  banco: '',
  limite: '0',
  dia_fechamento: '1',
  dia_vencimento: '10',
  conta_pagamento_id: '',
};

const INITIAL_PURCHASE = {
  cartao_id: '',
  fornecedor_nome: '',
  descricao: '',
  documento: '',
  data_compra: new Date().toISOString().slice(0, 10),
  valor_total: '',
  parcelas_total: '1',
  categoria_id: '',
  obra_id: '',
  etapa_id: '',
  arquivo_id: '',
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
};

const toOptions = (items, labelKeys = ['nome']) => (items || []).map((item) => ({
  value: item.id,
  label: labelKeys.map((key) => item[key]).filter(Boolean).join(' - ') || item.id,
}));

export const FinanceiroCartoes = () => {
  const [cartoes, setCartoes] = useState([]);
  const [faturas, setFaturas] = useState([]);
  const [compras, setCompras] = useState([]);
  const [options, setOptions] = useState({ contas: [], categorias: [], obras: [], etapas: [] });
  const [isCardModalOpen, setIsCardModalOpen] = useState(false);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [cardForm, setCardForm] = useState(INITIAL_CARD);
  const [purchaseForm, setPurchaseForm] = useState(INITIAL_PURCHASE);
  const [purchasePdfFile, setPurchasePdfFile] = useState(null);
  const [purchasePdfPreview, setPurchasePdfPreview] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importingPurchasePdf, setImportingPurchasePdf] = useState(false);
  const [payingId, setPayingId] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cartoesRes, faturasRes, comprasRes, contasRes, categoriasRes, obrasRes] = await Promise.all([
        api.get('/contabilidade/cartoes-credito'),
        api.get('/contabilidade/cartoes-credito/faturas'),
        api.get('/contabilidade/cartoes-credito/compras'),
        api.get('/contabilidade/contas-bancarias'),
        api.get('/contabilidade/plano-contas', { params: { tipo: 'despesa' } }),
        api.get('/construtora/obras', { params: { per_page: 200 } }),
      ]);
      setCartoes(cartoesRes.data.items || []);
      setFaturas(faturasRes.data.items || []);
      setCompras(comprasRes.data.items || []);
      setOptions((current) => ({
        ...current,
        contas: toOptions(contasRes.data.items, ['nome', 'banco']),
        categorias: toOptions(categoriasRes.data.items, ['codigo', 'nome']),
        obras: toOptions(obrasRes.data.items, ['nome']),
      }));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar cartoes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totals = useMemo(() => ({
    aberto: faturas.filter((item) => item.status !== 'paga').reduce((acc, item) => acc + Number(item.valor_total || 0), 0),
    pago: faturas.filter((item) => item.status === 'paga').reduce((acc, item) => acc + Number(item.valor_total || 0), 0),
    compras: compras.reduce((acc, item) => acc + Number(item.valor_total || 0), 0),
  }), [compras, faturas]);

  const handleCardChange = (field, value) => {
    setCardForm((current) => ({ ...current, [field]: value }));
  };

  const handlePurchaseChange = (field, value) => {
    setPurchaseForm((current) => ({ ...current, [field]: value }));
  };

  const closePurchaseModal = () => {
    setIsPurchaseModalOpen(false);
    setPurchasePdfFile(null);
    setPurchasePdfPreview(null);
    setPurchaseForm(INITIAL_PURCHASE);
  };

  const handlePurchaseObraChange = async (value) => {
    setPurchaseForm((current) => ({ ...current, obra_id: value, etapa_id: '' }));
    if (!value) {
      setOptions((current) => ({ ...current, etapas: [] }));
      return;
    }
    try {
      const response = await api.get(`/construtora/obras/${value}/etapas`);
      setOptions((current) => ({
        ...current,
        etapas: toOptions(response.data.etapas, ['nome']),
      }));
    } catch (err) {
      setOptions((current) => ({ ...current, etapas: [] }));
    }
  };

  const handleCreateCard = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/contabilidade/cartoes-credito', {
        ...cardForm,
        limite: Number(String(cardForm.limite).replace(',', '.')),
        dia_fechamento: Number(cardForm.dia_fechamento),
        dia_vencimento: Number(cardForm.dia_vencimento),
      });
      setIsCardModalOpen(false);
      setCardForm(INITIAL_CARD);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar cartao.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreatePurchase = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post('/contabilidade/cartoes-credito/compras', {
        ...purchaseForm,
        valor_total: Number(String(purchaseForm.valor_total).replace(',', '.')),
        parcelas_total: Number(purchaseForm.parcelas_total),
      });
      closePurchaseModal();
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar compra.');
    } finally {
      setSaving(false);
    }
  };

  const handleImportPurchasePdf = async () => {
    if (!purchasePdfFile) {
      setError('Selecione um PDF para importar.');
      return;
    }
    setImportingPurchasePdf(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('arquivo', purchasePdfFile);
      if (purchaseForm.obra_id) formData.append('obra_id', purchaseForm.obra_id);
      const response = await api.post('/contabilidade/cartoes-credito/compras/importar-pdf', formData);
      const extracted = response.data.extracted || {};
      const parcelas = extracted.parcelas || [];
      const parcelasTotal = parcelas.length
        ? String(Math.max(...parcelas.map((item) => Number(item.total || 1))))
        : purchaseForm.parcelas_total;

      setPurchasePdfPreview(response.data);
      setPurchaseForm((current) => ({
        ...current,
        arquivo_id: response.data.arquivo?.id || '',
        fornecedor_nome: extracted.fornecedor_nome || current.fornecedor_nome,
        documento: extracted.documento || current.documento,
        data_compra: extracted.data_compra || current.data_compra,
        valor_total: extracted.valor ? String(extracted.valor) : current.valor_total,
        parcelas_total: parcelasTotal,
        descricao: extracted.descricao || current.descricao,
      }));
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao importar PDF da compra.');
    } finally {
      setImportingPurchasePdf(false);
    }
  };

  const handlePayInvoice = async (fatura) => {
    setPayingId(fatura.id);
    setError('');
    try {
      await api.post(`/contabilidade/cartoes-credito/faturas/${fatura.id}/pagar`, {
        data_pagamento: new Date().toISOString().slice(0, 10),
      });
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao pagar fatura.');
    } finally {
      setPayingId('');
    }
  };

  const faturasColumns = [
    { key: 'cartao', title: 'Cartao', accessor: 'cartao' },
    { key: 'mes', title: 'Mes', accessor: 'mes_referencia' },
    { key: 'vencimento', title: 'Vencimento', render: (row) => formatDate(row.data_vencimento) },
    { key: 'valor', title: 'Valor', render: (row) => <span className="text-red-400">{formatCurrency(row.valor_total)}</span> },
    { key: 'parcelas', title: 'Parcelas', render: (row) => `${row.parcelas_pagas || 0}/${row.parcelas_count || 0}` },
    {
      key: 'status',
      title: 'Status',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${row.status === 'paga' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
          {row.status}
        </span>
      ),
    },
    {
      key: 'acoes',
      title: 'Acoes',
      render: (row) => (
        <div className="flex items-center gap-2">
          <GlassButton size="xs" variant="secondary" icon={Eye} onClick={() => setSelectedInvoice(row)}>
            Ver
          </GlassButton>
          {row.status === 'paga' ? (
            <span className="text-xs text-white/40">Paga</span>
          ) : (
            <GlassButton size="xs" icon={CheckCircle} loading={payingId === row.id} onClick={() => handlePayInvoice(row)}>
              Pagar
            </GlassButton>
          )}
        </div>
      ),
    },
  ];

  const comprasColumns = [
    { key: 'descricao', title: 'Compra', render: (row) => (
      <div>
        <p className="text-white/90">{row.descricao}</p>
        <p className="text-xs text-white/50">{row.fornecedor_nome || row.cartao}</p>
      </div>
    ) },
    { key: 'data', title: 'Data', render: (row) => formatDate(row.data_compra) },
    { key: 'obra', title: 'Obra', render: (row) => row.obra || '-' },
    { key: 'parcelas', title: 'Parcelas', render: (row) => `${row.parcelas_total}x` },
    { key: 'arquivo', title: 'Anexo', render: (row) => row.arquivo ? 'PDF' : '-' },
    { key: 'valor', title: 'Valor', render: (row) => formatCurrency(row.valor_total) },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Cartao de Credito</h1>
          <p className="text-white/50">Competencia da obra separada do pagamento da fatura</p>
        </div>
        <div className="flex items-center gap-3">
          <GlassButton variant="secondary" icon={RefreshCw} onClick={loadData} loading={loading}>Atualizar</GlassButton>
          <GlassButton variant="secondary" icon={CreditCard} onClick={() => setIsCardModalOpen(true)}>Novo cartao</GlassButton>
          <GlassButton icon={Plus} onClick={() => setIsPurchaseModalOpen(true)}>Nova compra</GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GlassCard className="p-6">
          <p className="text-sm text-white/50">Faturas abertas</p>
          <p className="text-2xl font-bold text-yellow-400">{formatCurrency(totals.aberto)}</p>
        </GlassCard>
        <GlassCard className="p-6">
          <p className="text-sm text-white/50">Compras registradas</p>
          <p className="text-2xl font-bold text-white/90">{formatCurrency(totals.compras)}</p>
        </GlassCard>
        <GlassCard className="p-6">
          <p className="text-sm text-white/50">Faturas pagas</p>
          <p className="text-2xl font-bold text-green-400">{formatCurrency(totals.pago)}</p>
        </GlassCard>
      </div>

      <FadeIn>
        <GlassCard>
          <div className="p-6 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white/90">Faturas</h3>
          </div>
          <GlassTable columns={faturasColumns} data={faturas} searchable={false} emptyMessage={loading ? 'Carregando faturas...' : 'Nenhuma fatura registrada'} />
        </GlassCard>
      </FadeIn>

      <FadeIn delay={0.1}>
        <GlassCard>
          <div className="p-6 border-b border-white/10">
            <h3 className="text-lg font-semibold text-white/90">Compras</h3>
          </div>
          <GlassTable columns={comprasColumns} data={compras} searchable={false} emptyMessage={loading ? 'Carregando compras...' : 'Nenhuma compra registrada'} />
        </GlassCard>
      </FadeIn>

      <GlassModal
        isOpen={isCardModalOpen}
        onClose={() => setIsCardModalOpen(false)}
        title="Novo Cartao"
        icon={CreditCard}
        size="lg"
        footer={<><GlassButton variant="ghost" onClick={() => setIsCardModalOpen(false)}>Cancelar</GlassButton><GlassButton onClick={handleCreateCard} loading={saving}>Salvar</GlassButton></>}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput label="Nome" value={cardForm.nome} onChange={(e) => handleCardChange('nome', e.target.value)} placeholder="Cartao da obra" />
          <GlassInput label="Banco" value={cardForm.banco} onChange={(e) => handleCardChange('banco', e.target.value)} placeholder="Banco" />
          <GlassInput label="Limite" type="number" step="0.01" value={cardForm.limite} onChange={(e) => handleCardChange('limite', e.target.value)} />
          <GlassInput label="Dia fechamento" type="number" min="1" max="31" value={cardForm.dia_fechamento} onChange={(e) => handleCardChange('dia_fechamento', e.target.value)} />
          <GlassInput label="Dia vencimento" type="number" min="1" max="31" value={cardForm.dia_vencimento} onChange={(e) => handleCardChange('dia_vencimento', e.target.value)} />
          <GlassSelect label="Conta para pagamento" value={cardForm.conta_pagamento_id} onChange={(e) => handleCardChange('conta_pagamento_id', e.target.value)} placeholder="Selecionar ao pagar" options={options.contas} />
        </div>
      </GlassModal>

      <GlassModal
        isOpen={isPurchaseModalOpen}
        onClose={closePurchaseModal}
        title="Nova Compra no Cartao"
        icon={Receipt}
        size="xl"
        footer={<><GlassButton variant="ghost" onClick={closePurchaseModal}>Cancelar</GlassButton><GlassButton onClick={handleCreatePurchase} loading={saving}>Salvar compra</GlassButton></>}
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
            <GlassInput
              label="PDF da nota/recibo"
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => {
                setPurchasePdfFile(e.target.files?.[0] || null);
                setPurchasePdfPreview(null);
                handlePurchaseChange('arquivo_id', '');
              }}
            />
            <GlassButton icon={Upload} onClick={handleImportPurchasePdf} loading={importingPurchasePdf}>
              Ler PDF
            </GlassButton>
          </div>

          {purchasePdfPreview?.extracted && (
            <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-white/50">Confianca da leitura</p>
                  <p className="text-xl font-semibold text-white/90">{purchasePdfPreview.extracted.confidence || 0}%</p>
                </div>
                <p className="text-xs text-white/40 text-right max-w-md">
                  Revise os campos antes de salvar. A compra continua sem baixar caixa; o caixa sai apenas no pagamento da fatura.
                </p>
              </div>
            </GlassCard>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassSelect label="Cartao" value={purchaseForm.cartao_id} onChange={(e) => handlePurchaseChange('cartao_id', e.target.value)} placeholder="Selecione" options={cartoes.map((cartao) => ({ value: cartao.id, label: cartao.nome }))} />
          <GlassInput label="Data da compra" type="date" value={purchaseForm.data_compra} onChange={(e) => handlePurchaseChange('data_compra', e.target.value)} />
          <GlassInput label="Fornecedor" value={purchaseForm.fornecedor_nome} onChange={(e) => handlePurchaseChange('fornecedor_nome', e.target.value)} />
          <GlassInput label="Documento" value={purchaseForm.documento} onChange={(e) => handlePurchaseChange('documento', e.target.value)} />
          <GlassInput label="Valor total" type="number" step="0.01" value={purchaseForm.valor_total} onChange={(e) => handlePurchaseChange('valor_total', e.target.value)} />
          <GlassInput label="Parcelas" type="number" min="1" max="48" value={purchaseForm.parcelas_total} onChange={(e) => handlePurchaseChange('parcelas_total', e.target.value)} />
          <GlassSelect label="Plano de contas" value={purchaseForm.categoria_id} onChange={(e) => handlePurchaseChange('categoria_id', e.target.value)} placeholder="Sem categoria" options={options.categorias} />
          <GlassSelect label="Obra" value={purchaseForm.obra_id} onChange={(e) => handlePurchaseObraChange(e.target.value)} placeholder="Sem obra" options={options.obras} />
          <GlassSelect label="Etapa" value={purchaseForm.etapa_id} onChange={(e) => handlePurchaseChange('etapa_id', e.target.value)} placeholder="Sem etapa" options={options.etapas} />
          <div className="md:col-span-2">
            <GlassTextarea label="Descricao" rows={3} value={purchaseForm.descricao} onChange={(e) => handlePurchaseChange('descricao', e.target.value)} placeholder="Compra de material, ferramenta, servico..." />
          </div>
          </div>
        </div>
      </GlassModal>

      <GlassModal
        isOpen={Boolean(selectedInvoice)}
        onClose={() => setSelectedInvoice(null)}
        title={`Fatura ${selectedInvoice?.mes_referencia || ''}`}
        icon={Receipt}
        size="xl"
        footer={<GlassButton variant="ghost" onClick={() => setSelectedInvoice(null)}>Fechar</GlassButton>}
      >
        {selectedInvoice && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <GlassCard className="p-4" hover={false}>
                <p className="text-xs text-white/50">Cartao</p>
                <p className="text-sm font-semibold text-white/90">{selectedInvoice.cartao || '-'}</p>
              </GlassCard>
              <GlassCard className="p-4" hover={false}>
                <p className="text-xs text-white/50">Vencimento</p>
                <p className="text-sm font-semibold text-white/90">{formatDate(selectedInvoice.data_vencimento)}</p>
              </GlassCard>
              <GlassCard className="p-4" hover={false}>
                <p className="text-xs text-white/50">Valor</p>
                <p className="text-sm font-semibold text-red-300">{formatCurrency(selectedInvoice.valor_total)}</p>
              </GlassCard>
              <GlassCard className="p-4" hover={false}>
                <p className="text-xs text-white/50">Status</p>
                <p className="text-sm font-semibold text-white/90">{selectedInvoice.status}</p>
              </GlassCard>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left text-white/50">
                    <th className="py-3 pr-3 font-medium">Compra</th>
                    <th className="py-3 pr-3 font-medium">Parcela</th>
                    <th className="py-3 pr-3 font-medium">Competencia</th>
                    <th className="py-3 pr-3 font-medium">Obra</th>
                    <th className="py-3 pr-3 font-medium text-right">Valor</th>
                    <th className="py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedInvoice.parcelas || []).map((parcela) => (
                    <tr key={parcela.id} className="border-b border-white/5">
                      <td className="py-3 pr-3">
                        <p className="text-white/90">{parcela.compra?.descricao || '-'}</p>
                        <p className="text-xs text-white/45">{parcela.compra?.fornecedor_nome || '-'}</p>
                      </td>
                      <td className="py-3 pr-3 text-white/70">{parcela.numero}</td>
                      <td className="py-3 pr-3 text-white/70">{formatDate(parcela.data_competencia)}</td>
                      <td className="py-3 pr-3 text-white/70">{parcela.compra?.obra || '-'}</td>
                      <td className="py-3 pr-3 text-right text-white/90">{formatCurrency(parcela.valor)}</td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${parcela.status === 'paga' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {parcela.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!selectedInvoice.parcelas || selectedInvoice.parcelas.length === 0) && (
                <p className="py-6 text-center text-sm text-white/50">Nenhuma parcela nesta fatura.</p>
              )}
            </div>
          </div>
        )}
      </GlassModal>
    </div>
  );
};

export default FinanceiroCartoes;
